import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, ChangeDetectorRef, ViewChild, TemplateRef, AfterViewInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormioCustomComponent, FormioEvent } from '@app/shared/elements.common';
import { HttpService } from '@app/@core/services/http.service';
import { MyApplicationsService } from '@app/my-application/my-app-service.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs';
import { LocalService } from '@app/@core/services/local.service';
import { FormioService } from '@pics-core/dynamic';

@Component({
  standalone: false,
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss']
})
export class CustomSelectComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy, FormioCustomComponent<any> {
  @Input() value: any;
  @Input() disabled: boolean = false;
  @Input() name: string = '';
  @Input() readOnlyCondition: string = '';

  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() formioEvent = new EventEmitter<FormioEvent>();

  @ViewChild('appTypeNoticeModal') appTypeNoticeModal: any;

  currApplicationType: string = '';
  lookupData: any[] = [];
  applicationTypes: any[] = [];
  selectedValue: string = '';
  touched: boolean = false;
  showSpecificError: boolean = false;
  specificErrorMessage: string = '';
  readOnlyMode: boolean = false;
  private formData: any = {};
  private errorMessages: { [key: string]: string } = {};

  // Modal properties
  modalRef: NgbModalRef;
  noticeHeading: string = '';
  noticeContent: string = '';
  buttonText: string = '';
  showProceedButton: boolean = false;
  applicationID: any = null;
  is30CreditsEarned: boolean = false;
  is30CreditsEarnedAnswered: boolean = false;
  private modalShownForCurrentSelection: boolean = false;
  private apiDataLoaded: boolean = false;
  private destroy$ = new Subject<void>();
  private academicInfoData: any[] = [];

  constructor(
    private readonly modalService: NgbModal,
    private readonly httpService: HttpService,
    private readonly cdr: ChangeDetectorRef,
    private readonly myApplicationService: MyApplicationsService,
    private sessionStorage: LocalService,
    private readonly formIoService: FormioService
  ) {}

  ngOnInit(): void {
    this.getCurrApplicationTypeFromSession();
    this.loadLookupData();
    this.subscribeToFormData();
    const applicationIDString = this.sessionStorage.getItem('applicationID');
    this.applicationID = applicationIDString ? JSON.parse(applicationIDString) : null;

    // Don't set selectedValue or emit here - wait for loadApplicationEducationData to validate against the API first

    this.formData = this.formIoService.getCurrentFormData();
    this.checkReadOnlyMode();
    this.formIoService.getFormData().subscribe(data => {
      this.formData = data;
      this.checkReadOnlyMode();
    });
  }

  ngAfterViewInit(): void {
    if (!this.appTypeNoticeModal) {
      console.error('Modal template not accessible');
    }
  }

    ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange) {
      const newValue = changes['value'].currentValue;

      // Don't let external values override until API data has loaded
      if (!this.apiDataLoaded) {
        return;
      }

      // If API has loaded and cleared the value (no data in DB), don't let external default override it
      if (!this.selectedValue && newValue) {
        return;
      }

      if (newValue) {
        // Handle both old format (string) and new format (object with key/value)
        let selectedKey = '';
        if (typeof newValue === 'string') {
          selectedKey = newValue;
        } else if (typeof newValue === 'object' && newValue.key) {
          selectedKey = newValue.key;
        }

        if (selectedKey && selectedKey !== this.selectedValue) {
          this.selectedValue = selectedKey;

          this.value = selectedKey;
          this.cdr.detectChanges();
          if (this.lookupData.length > 0) {
            this.checkModalForValue(selectedKey, false);
          }
        }
      }
    }

    if (changes['readOnlyCondition'] && changes['readOnlyCondition'].currentValue !== changes['readOnlyCondition'].previousValue) {
      this.checkReadOnlyMode();
    }
  }

  private checkModalForValue(selectedKey: string, shouldEmit: boolean = true): void {
    const selectedItem = this.lookupData.find(item => item.key === selectedKey);
    const highestEducationValue = selectedItem ? selectedItem.value : '';

    if (highestEducationValue) {
      this.sessionStorage.setItem('highestEducation', highestEducationValue);
      this.sessionStorage.setItem('highestEducationKey', selectedKey);
      this.value = selectedKey;
      if (shouldEmit) {
      this.valueChange.emit(this.value);
      }
      this.checkAndShowModal(highestEducationValue);
    }
  }

  getCurrApplicationTypeFromSession(): void {
    let appType = this.sessionStorage.getItem('currApplicationType') || '';

    if (appType.startsWith('"') && appType.endsWith('"')) {
      appType = appType.slice(1, -1);
    }

    this.currApplicationType = appType;
  }

  loadLookupData(): void {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=HIGHEST LEVEL OF EDUCATION'),
      this.httpService.get('/domain/lookup?requestType=APPLICATION_TYPES'),
      this.httpService.get('/domain/lookup?requestType=MESSAGES')
    ]).subscribe({
      next: (responses: any[]) => {
        this.lookupData = responses[0].data || [];
        this.applicationTypes = responses[1].data || [];

        // Process error messages from lookup
        const errorMessagesData = responses[2]?.data || [];
        console.log('[loadLookupData] errorMessagesData:', errorMessagesData);
        if (Array.isArray(errorMessagesData)) {
          errorMessagesData.forEach((message: { key: string; value: string }) => {
            if (message.key && message.value !== undefined) {
              this.errorMessages[message.key] = message.value;
            }
          });
        }
        console.log('[loadLookupData] errorMessages:', this.errorMessages);

        this.cdr.detectChanges();

        // Load application education data after lookup data is available
        this.loadApplicationEducationData();
      },
      error: (error) => {
        console.error('Error loading lookup data:', error);
        this.lookupData = [];
        this.applicationTypes = [];
        this.cdr.detectChanges();
      }
    });
  }

  loadApplicationEducationData(): void {
    if (!this.applicationID) {
      this.apiDataLoaded = true;
      // No applicationID means no saved data - clear like clearSelection()
      console.log('[loadApplicationEducationData] No applicationID - clearing selection');
      this.resetToEmpty();
      return;
    }

    this.httpService.get(`/application-flow/appEducationData?applicationId=${this.applicationID}`).subscribe({
      next: (response: any) => {
        this.apiDataLoaded = true;
        console.log('[loadApplicationEducationData] API Response:', response);
        if (response.status === 'success' && response.data && response.data.success && response.data.data && response.data.data.length > 0) {
          const educationData = response.data.data[0];
          const cdHighestEducation = educationData.cd_highest_education;
          console.log('[loadApplicationEducationData] cdHighestEducation from API:', cdHighestEducation);

          // Set the selected value based on the API response
          if (cdHighestEducation) {
            this.selectedValue = cdHighestEducation;
            this.value = cdHighestEducation;
            this.cdr.detectChanges();

            // Refresh academicInfo from current form data before validation
            this.refreshAcademicInfoData();

            // Validate against academicInfo on load
            if (!this.validateAgainstAcademicInfo(cdHighestEducation)) {
              this.value = '';
              this.valueChange.emit(this.value);
              this.cdr.detectChanges();
              return;
            }

            // Emit the API-validated value
            console.log('[loadApplicationEducationData] Emitting API value:', cdHighestEducation);
            this.valueChange.emit(cdHighestEducation);

            // Check if we need to show modal after setting the value
            if (this.lookupData.length > 0) {
              this.checkModalForValue(cdHighestEducation, false);
            }
          } else {
            // API returned data but cd_highest_education is empty - clear like clearSelection()
            console.log('[loadApplicationEducationData] cd_highest_education is empty - clearing');
            this.resetToEmpty();
          }
        } else {
          // API returned success:false or no data - clear like clearSelection()
          console.log('[loadApplicationEducationData] No data in DB (success:false) - clearing pre-set value');
          this.resetToEmpty();
        }
      },
      error: (error) => {
        this.apiDataLoaded = true;
        console.error('[loadApplicationEducationData] Error:', error);
        // On error, clear like clearSelection()
        this.resetToEmpty();
      }
    });
  }

  onSelectionChange(event: any): void {
    const selectedKey = event.target.value;
    this.selectedValue = selectedKey;
    this.touched = true;

    // Reset modal shown flag when user changes selection
    this.modalShownForCurrentSelection = false;

    // Clear specific error when user makes a selection
    this.clearSpecificError();

    // Refresh academicInfo from current form data before validation
    this.refreshAcademicInfoData();

    // Validate against academicInfo - if validation fails, show error and don't emit
    const academicInfoValid = this.validateAgainstAcademicInfo(selectedKey);

    const selectedItem = this.lookupData.find(item => item.key === selectedKey);
    const highestEducationValue = selectedItem ? selectedItem.value : '';

    if (highestEducationValue) {
      this.sessionStorage.setItem('highestEducation', highestEducationValue);
      this.sessionStorage.setItem('highestEducationKey', selectedKey);
    }

    // If academicInfo validation failed, don't emit value
    if (!academicInfoValid) {
      this.value = '';
      this.valueChange.emit(this.value);
      this.cdr.detectChanges();
      return;
    }

    this.value = selectedKey;
    this.valueChange.emit(this.value);
    this.cdr.detectChanges();
    this.checkAndShowModal(highestEducationValue);
  }

  onFocus(): void {
    // Mark as touched when user focuses on the field
  }

  onBlur(): void {
    this.touched = true;
    this.cdr.detectChanges();
  }

  clearSelection(): void {
    this.touched = true;
    this.resetToEmpty();
  }

  private resetToEmpty(): void {
    this.selectedValue = '';
    this.value = '';
    this.modalShownForCurrentSelection = false;
    this.sessionStorage.removeItem('highestEducation');
    this.sessionStorage.removeItem('highestEducationKey');
    this.clearSpecificError();
    this.valueChange.emit(this.value);
    this.cdr.detectChanges();
  }

  getSelectedValueText(): string {
    const selectedItem = this.lookupData.find(item => item.key === this.selectedValue);
    return selectedItem ? selectedItem.value : '';
  }

  trackByFn(index: number, item: any): any {
    return item.key || index;
  }

  private checkAndShowModal(highestEducation: string): void {

    if (!this.currApplicationType) {
      this.getCurrApplicationTypeFromSession();
    }

    const isHighSchoolOrGED = highestEducation === 'High School Diploma or Equivalent';
    const isAssociateDegree = highestEducation === 'Associate Degree';
    const isBelowBachelors = isHighSchoolOrGED || isAssociateDegree;
    let shouldShowModal = false;

    // For Substitute: show popup only if High School selected AND credits earned question is answered AND is_30_credits_earned is false
    if (this.currApplicationType === 'Substitute' && isHighSchoolOrGED && this.is30CreditsEarnedAnswered && !this.is30CreditsEarned) {
      shouldShowModal = true;
    }
    else if ((this.currApplicationType === 'ARL (Alternative Route to Licensure)' || this.currApplicationType === 'ARL + Substitute' ||  this.currApplicationType === 'Substitute + ARL'  ) && isBelowBachelors) {
      shouldShowModal = true;
    }
    else if (this.currApplicationType === 'Emergency Substitute' && !isHighSchoolOrGED) {
      shouldShowModal = true;
    }

    if (shouldShowModal) {
      // Only show modal if not already shown for current selection
      if (!this.modalShownForCurrentSelection) {
        this.modalShownForCurrentSelection = true;
        this.openAppTypeNoticeModal();
      }
    }
  }

  private openAppTypeNoticeModal(): void {
    if (!this.appTypeNoticeModal) {
      console.error('Modal template not found');
      return;
    }

    this.setNoticeContent();

    try {
      this.modalRef = this.modalService.open(this.appTypeNoticeModal, {
        size: 'md',
        centered: true,
        backdrop: 'static'
      });

      this.modalRef.result.then((result) => {
        if (result === 'proceed') {
          this.handleApplicationTypeChange();
        }
      }).catch(() => {
        // Modal was dismissed - emit empty value but keep selectedValue for display
        this.value = '';
        this.touched = true;
        this.valueChange.emit(this.value);

        // Reset credits earned tracking
        this.is30CreditsEarned = false;
        this.is30CreditsEarnedAnswered = false;

        // Emit event to clear is_30_credits_earned in the form
        this.formioEvent.emit({
          eventName: 'clearCreditsEarned',
          data: { is_30_credits_earned: null }
        });

        // Set specific error message based on application type
        this.setSpecificErrorMessage();

        this.cdr.detectChanges();
      });
    } catch (error) {
      console.error('Error opening modal:', error);
    }
  }

  private setNoticeContent(): void {
    const selectedEducation = this.getSelectedValueText();
    const isHighSchoolOrGED = selectedEducation === 'High School Diploma or Equivalent';
    const isAssociateDegree = selectedEducation === 'Associate Degree';
    const isBelowBachelors = isHighSchoolOrGED || isAssociateDegree;

    if (this.currApplicationType === 'Emergency Substitute' && !isHighSchoolOrGED) {
      this.noticeHeading = 'You have selected an education level above High School.';
      this.noticeContent = 'This application will be processed as a Substitute application rather than an Emergency Substitute application. Your current progress will be retained.';
      this.buttonText = 'Proceed with Substitute Application';
      this.showProceedButton = true;
    }
    else if (this.currApplicationType === 'Substitute' && isHighSchoolOrGED) {
      this.noticeHeading = 'You have selected High School as your highest level of education.';
      this.noticeContent = 'This application will be processed as an Emergency Substitute application rather than a Substitute application. Your current progress will be retained.';
      this.buttonText = 'Proceed with Emergency Substitute Application';
      this.showProceedButton = true;
    }
    else if ((this.currApplicationType === 'ARL (Alternative Route to Licensure)' || this.currApplicationType === 'ARL + Substitute' ||  this.currApplicationType === 'Substitute + ARL') && isBelowBachelors) {
      this.noticeHeading = `You have selected ${selectedEducation} as your highest level completed. ARL applications require a bachelor's degree or higher.`;
      this.noticeContent = `Please review your education level and make any necessary changes to continue with this application.`;
      this.buttonText = '';
      this.showProceedButton = false;
    }
    else {
      this.noticeHeading = 'You have selected High School as your highest level of education.';
      this.noticeContent = 'This application will be processed as an Emergency Substitute application rather than a Substitute application. Your current progress will be retained.';
      this.buttonText = 'Proceed with Emergency Substitute Application';
      this.showProceedButton = true;
    }
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.dismiss();
      // Emit empty value but keep selectedValue for display
      this.value = '';
      this.touched = true;
      this.valueChange.emit(this.value);

      // Reset credits earned tracking
      this.is30CreditsEarned = false;
      this.is30CreditsEarnedAnswered = false;

      // Emit event to clear is_30_credits_earned in the form
      this.formioEvent.emit({
        eventName: 'clearCreditsEarned',
        data: { is_30_credits_earned: null }
      });

      // Set specific error message based on application type
      this.setSpecificErrorMessage();

      this.cdr.detectChanges();
    } else {
      this.modalService.dismissAll();
    }
  }

  proceedWithApplication(): void {
    if (this.modalRef) {
      let request = {
        application_id: this.applicationID
      };
      this.myApplicationService.applicationConversion(request).subscribe({
        next: response => {
          console.log('Application conversion response:', response);

          if (response.status === 'success' && response.data && response.data.length > 0) {
            const updatedApplicationTypeKey = response.data[0].p_updated_application_type;

            const matchedApplicationType = this.applicationTypes.find(appType => appType.key === updatedApplicationTypeKey);

            if (matchedApplicationType) {
              const appTypeValue = matchedApplicationType.value === 'Regular - Initial' ? 'Regular-initial' : matchedApplicationType.value;

              this.sessionStorage.setItem('currApplicationType', JSON.stringify(matchedApplicationType.value));
              this.sessionStorage.setItem('applicationType', JSON.stringify(appTypeValue));

              // Get existing navigationData and preserve other banner header fields
              let navigationData = this.sessionStorage.getObj('navigationData') || {};

              // Ensure bannerheader exists
              if (!navigationData.bannerheader) {
                navigationData.bannerheader = {};
              }

              // Only update the Application Type, preserve other fields
              navigationData.bannerheader["Application Type"] = matchedApplicationType.value;

              this.sessionStorage.setObj('navigationData',navigationData)
              // Update the local currApplicationType property
              this.currApplicationType = matchedApplicationType.value;

              console.log('Updated application type:', matchedApplicationType.value);
              console.log('Updated session storage with new application type');
            }
          }

          // Clear specific error when proceeding successfully
          this.clearSpecificError();

          this.modalRef.close('proceed');
        },
        error: error => {
          console.error('Error converting application:', error);
        }
      });
    }
  }

  private handleApplicationTypeChange(): void {
    const isHighSchoolOrGED = this.getSelectedValueText() === 'High School Diploma or Equivalent';
    let newApplicationType = '';

    if (this.currApplicationType === 'Emergency Substitute' && !isHighSchoolOrGED) {
      newApplicationType = 'Substitute';
    } else if (this.currApplicationType === 'Substitute' && isHighSchoolOrGED) {
      newApplicationType = 'Emergency Substitute';
    }

    if (newApplicationType) {
      // Find the matching application type from the lookup data
      const matchedApplicationType = this.applicationTypes.find(appType => appType.value === newApplicationType);

      if (matchedApplicationType) {
        const appTypeValue = matchedApplicationType.value === 'Regular - Initial' ? 'Regular-initial' : matchedApplicationType.value;

        this.sessionStorage.setItem('currApplicationType', JSON.stringify(matchedApplicationType.value));
        this.sessionStorage.setItem('applicationType', JSON.stringify(appTypeValue));
        // Get existing navigationData and preserve other banner header fields
        let navigationData = this.sessionStorage.getObj('navigationData') || {};

        // Ensure bannerheader exists
        if (!navigationData.bannerheader) {
          navigationData.bannerheader = {};
        }

        // Only update the Application Type, preserve other fields
        navigationData.bannerheader["Application Type"] = matchedApplicationType.value;

        this.sessionStorage.setObj('navigationData',navigationData)
        // Update the local currApplicationType property
        this.currApplicationType = matchedApplicationType.value;
      }
    }

    // if (this.formioEvent) {
    //   this.formioEvent.emit({
    //     eventName: 'applicationTypeChanged',
    //     data: {
    //       oldType: this.currApplicationType,
    //       newType: sessionStorage.getItem('currApplicationType'),
    //       educationLevel: this.getSelectedValueText()
    //     }
    //   });
    // }
  }

  private setSpecificErrorMessage(): void {
    this.showSpecificError = true;

    if (this.currApplicationType === 'Emergency Substitute') {
      this.specificErrorMessage = this.errorMessages['M0082'] || 'Please select high school or equivalent to continue.';
    } else if (this.currApplicationType === 'ARL (Alternative Route to Licensure)' || this.currApplicationType === 'ARL + Substitute' || this.currApplicationType === 'Substitute + ARL') {
      this.specificErrorMessage = this.errorMessages['M0081'] || 'Please select a bachelor\'s degree or higher to continue.';
    } else if (this.currApplicationType === 'Substitute') {
      this.specificErrorMessage = this.errorMessages['M0149'] || 'Please select an associate\'s degree or higher to continue.';
    } else {
      this.showSpecificError = false;
      this.specificErrorMessage = '';
    }
  }

  private clearSpecificError(): void {
    this.showSpecificError = false;
    this.specificErrorMessage = '';
  }

  private refreshAcademicInfoData(): void {
    const currentFormData = this.formIoService.getCurrentFormData();
    const formData = currentFormData?.data ?? currentFormData;

    // academicInfo is nested: formData.academicInfo.academicInfo
    const academicInfoRaw = formData?.academicInfo?.academicInfo;
    this.academicInfoData = Array.isArray(academicInfoRaw) ? academicInfoRaw : [];
  }

  private validateAgainstAcademicInfo(selectedKey: string): boolean {
    if (!selectedKey) {
      return true;
    }

    if (!Array.isArray(this.academicInfoData) || this.academicInfoData.length === 0) {
      return true;
    }

    const selectedValue = parseInt(selectedKey, 10);
    if (isNaN(selectedValue)) {
      return true;
    }

    for (const item of this.academicInfoData) {
      const credentialType = parseInt(item.cd_credential_type, 10);
      if (!isNaN(credentialType) && credentialType > selectedValue) {
        this.showSpecificError = true;
        this.specificErrorMessage = this.errorMessages['M0047'] || 'Selected credential type cannot be higher than your highest level of education completed.';
        return false;
      }
    }

    return true;
  }

  private subscribeToFormData(): void {
    this.formIoService.getFormData().pipe(
      takeUntil(this.destroy$)
    ).subscribe(data => {
      const formData = data?.data ?? data;
      this.formData = data;

      // Get academicInfo from form data (nested: formData.academicInfo.academicInfo)
      const academicInfoRaw = formData?.academicInfo?.academicInfo;
      this.academicInfoData = Array.isArray(academicInfoRaw) ? academicInfoRaw : [];

      // Validate against academicInfo when form data changes
      if (this.selectedValue && this.apiDataLoaded) {
        if (!this.validateAgainstAcademicInfo(this.selectedValue)) {
          this.value = '';
          this.valueChange.emit(this.value);
          this.cdr.detectChanges();
        }
      }

      // Get is_30_credits_earned from application_highest_education
      const highestEducationRaw = formData?.application_highest_education;
      const highestEducation = Array.isArray(highestEducationRaw) ? highestEducationRaw[0] : highestEducationRaw;

      if (highestEducation) {
        const creditsEarned = highestEducation?.is_30_credits_earned;

        // Check if the field has been explicitly answered (not null/undefined)
        if (creditsEarned !== null && creditsEarned !== undefined && creditsEarned !== '') {
          const wasAnsweredBefore = this.is30CreditsEarnedAnswered;
          const previousValue = this.is30CreditsEarned;

          this.is30CreditsEarnedAnswered = true;
          this.is30CreditsEarned = creditsEarned === true || creditsEarned === 'true' || creditsEarned === 'Yes';

          // Reset modal flag if credits earned changed to Yes (user might change back to No)
          if (this.is30CreditsEarned) {
            this.modalShownForCurrentSelection = false;
            this.clearSpecificError();
          }

          // If credits earned just changed to false/No, check if we need to show modal
          // Only show if not already shown for this selection
          if (!this.is30CreditsEarned && this.selectedValue && !this.modalShownForCurrentSelection) {
            const selectedEducation = this.getSelectedValueText();
            if (selectedEducation === 'High School Diploma or Equivalent' && this.currApplicationType === 'Substitute') {
              this.modalShownForCurrentSelection = true;
              this.openAppTypeNoticeModal();
            }
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkReadOnlyMode(): void {
    if (this.readOnlyCondition) {
      try {
        const readOnlyScript = this.readOnlyCondition.replace(/sessionStorage/g, 'sessionStorageService');
        const prefix = 'var readOnlyCondition = false;';
        const suffix = ';return { readOnlyCondition: readOnlyCondition };';
        const fnDef = new Function('data', 'sessionStorageService', `${prefix}${readOnlyScript}${suffix}`);
        const result = fnDef(this.formData?.data || {}, this.sessionStorage);
        this.readOnlyMode = result.readOnlyCondition;
      } catch (e) {
        console.error('Error evaluating read only condition:', e);
        this.readOnlyMode = false;
      }
    } else {
      this.readOnlyMode = false;
    }
  }
}
